import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import type { DeliveryState } from "@/hooks/use-orders";
import { useCancelDelivery } from "@/hooks/use-orders";

const CONFIRM_PHRASE = "CANCELAR DELIVERY";

type CancelDeliveryDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  deliveryId: string;
  orderId: string;
  orderDailyNumber: number;
  deliveryState: DeliveryState;
  riderName: string | null;
  onCancelled?: () => void;
};

export function CancelDeliveryDialog({
  open,
  onOpenChange,
  deliveryId,
  orderId,
  orderDailyNumber,
  deliveryState,
  riderName,
  onCancelled,
}: CancelDeliveryDialogProps) {
  const { t } = useTranslation();
  const cancel = useCancelDelivery();
  const [reason, setReason] = useState("");
  const [confirmation, setConfirmation] = useState("");

  useEffect(() => {
    if (!open) {
      setReason("");
      setConfirmation("");
    }
  }, [open]);

  const requiresConfirmation = deliveryState === "en_route" || deliveryState === "arrived";
  const canSubmit = !requiresConfirmation || confirmation.trim() === CONFIRM_PHRASE;

  const handleConfirm = async () => {
    if (!canSubmit) {
      return;
    }
    try {
      await cancel.mutateAsync({
        deliveryId,
        orderId,
        reason: reason.trim() || undefined,
      });
      toast.success(
        t("orders.delivery.cancelSuccess", {
          defaultValue: "Delivery del pedido #{{number}} cancelado",
          number: orderDailyNumber,
        }),
      );
      onCancelled?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Action failed");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {t("orders.delivery.cancelTitle", {
              defaultValue: "Cancelar delivery",
            })}
          </DialogTitle>
          <DialogDescription>
            {requiresConfirmation
              ? t("orders.delivery.cancelExceptionalDescription", {
                  defaultValue:
                    "Estás cancelando un delivery en curso (#{{number}}). Esta acción no se puede deshacer y el rider será notificado.",
                  number: orderDailyNumber,
                })
              : t("orders.delivery.cancelDescription", {
                  defaultValue:
                    "Cancelar el delivery del pedido #{{number}}. El rider será notificado.",
                  number: orderDailyNumber,
                })}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {riderName ? (
            <div className="rounded-md border border-border bg-muted/30 px-3 py-2 text-sm">
              <span className="text-muted-foreground">
                {t("orders.delivery.assignedRider", { defaultValue: "Rider" })}:{" "}
              </span>
              <span className="font-medium">{riderName}</span>
            </div>
          ) : null}

          <div className="space-y-1">
            <Label htmlFor="cancel-delivery-reason">
              {t("orders.delivery.cancelReason", {
                defaultValue: "Motivo (opcional)",
              })}
            </Label>
            <Textarea
              id="cancel-delivery-reason"
              maxLength={500}
              onChange={(event) => setReason(event.target.value)}
              placeholder={t("orders.delivery.cancelReasonPlaceholder", {
                defaultValue: "Cliente canceló, dirección incorrecta, etc.",
              })}
              value={reason}
            />
          </div>

          {requiresConfirmation ? (
            <div className="space-y-1">
              <Label htmlFor="cancel-delivery-confirm">
                {t("orders.delivery.cancelConfirmLabel", {
                  defaultValue: 'Escribí "CANCELAR DELIVERY" para confirmar',
                })}
              </Label>
              <Input
                autoComplete="off"
                id="cancel-delivery-confirm"
                onChange={(event) => setConfirmation(event.target.value)}
                value={confirmation}
              />
            </div>
          ) : null}
        </div>

        <DialogFooter>
          <Button onClick={() => onOpenChange(false)} type="button" variant="outline">
            {t("common.cancel", { defaultValue: "Cancelar" })}
          </Button>
          <Button
            disabled={!canSubmit || cancel.isPending}
            onClick={handleConfirm}
            type="button"
            variant="destructive"
          >
            {t("orders.delivery.cancelConfirm", {
              defaultValue: "Confirmar cancelación",
            })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
